/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { VNode } from 'preact';
import { IconButton } from '../UNSAFE_IconButton';
import { CloseIcon } from '../PRIVATE_ThemedIcons/CloseIcon';
import { classNames } from '../utils/UNSAFE_classNames';
import { messageCloseButtonStyles } from './themes/MessageStyles.css';
import { MessageVariant } from './Message.types';

/**
 * Props for the MessageCloseButton component
 */
type Props = {
  /**
   * The callback function to be called when the close icon is clicked
   */
  onAction: (event?: Event) => void;

  /**
   * Button renderer
   */
  buttonRenderer?: (
    title: string,
    onAction: (event?: Event) => void,
    variant?: MessageVariant
  ) => VNode;

  /**
   * Style variant
   */
  variant?: MessageVariant;

  /**
   * Close button title
   */
  title?: string;
};

/**
 * A Component for rendering the message close button
 */
function MessageCloseButton({
  onAction,
  buttonRenderer,
  title = 'Close',
  variant = 'banner'
}: Props) {
  const renderedButton = buttonRenderer ? (
    buttonRenderer(title, onAction, variant)
  ) : (
    <IconButton size="sm" tooltip={title} variant="borderless" onAction={onAction}>
      <CloseIcon />
    </IconButton>
  );
  // TODO: JET-48715 - remove marker classes
  // Right now, oj-message-banner component uses this for unit tests and has no alternate way for
  // testing. This will need to be removed as we work on oj-c-message-banner.
  // TODO:  Refactor to utilize useComponentTheme and multiVariantStyles.

  const markerClassForTests = 'oj-c-messagebanner-close-icon';
  const classes = classNames([
    variant === 'banner' && markerClassForTests,
    messageCloseButtonStyles.base,
    messageCloseButtonStyles[variant]
  ]);

  // Otherwise, render the close icon
  return (
    <div class={classes} data-oj-message-close-button="">
      {renderedButton}
    </div>
  );
}

export { MessageCloseButton };
